import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import * as Api from '../../extViewmodels';
import { CsmMeetingTypes, IAccount, IUser } from '../../extViewmodels';
import { RealMagicAccountIds } from '../../extViewmodels/viewModels/index';
import { useDebounceCallback } from '../../hooks/useDebounceValue';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useMeetingLogStorage } from '../../hooks/useMeetingLogStorage';
import { useAdminUserSession } from '../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../models/hooks/appStateHooks';
import { useEventLogging } from '../../models/Logging';
import { createContentStateWithHtmlStringValue } from '../../models/utils/richContentUtils';
import { useAccountProfileCreateMutation } from '../../queries/AccountProfile/useAccountProfileCreateMutation';
import {
	invalidateAccountProfileQueries,
	useAccountProfileQuery,
} from '../../queries/AccountProfile/useAccountProfileQuery';
import { useAccountProfileUpdateMutation } from '../../queries/AccountProfile/useAccountProfileUpdateMutation';
import { useAdminAccountFilterQuery } from '../../queries/Admin/Account/useAdminAccountFilterQuery';
import { useAdminAccountQuery } from '../../queries/Admin/Account/useAdminAccountQuery';
import { useAdminAccountWebsiteUrlMutation } from '../../queries/Admin/Account/useAdminAccountWebsiteUrlMutation';
import { useAdminAccountProfileGenerateMutation } from '../../queries/Admin/AccountProfile/useAdminAccountProfileGenerateMutation';
import {
	useAdminAccountMeetingNoteMutation,
	useAdminContentNotesGenerateMutation,
	useAdminContentNotesSaveMutation,
	useAdminTranscriptSummarizeMutation,
} from '../../queries/Admin/Calendar';
import { useLoadAllUsers } from '../../queries/User/useLoadAllUsers';
import { hasContentPreferences } from '../../utils/hasContentPreferences';
import * as AdminApi from '../../viewmodels/AdminViewModels';
import {
	getContentHtmlStringValueFromRawRichTextContentState,
	IRichContentEditorState,
} from '../../viewmodels/AppViewModels';
import { ICalendarMeetingWithTranscripts, ITranscriptMatch } from '../../viewmodels/CalendarViewModels';
import { markdownToEditorHtml } from '../../web/components-v2/ai/markdownToEditorHtml';

const DEFAULT_MEETING_DURATION_MINUTES = 60;

type TeamType = 'SuccessClientMeeting' | 'ImplementationMeeting';
const TEAM_TYPE_STORAGE_KEY = 'meeting-notes-team-type';

export type RecordingPhase = 'idle' | 'summarizing' | 'form' | 'confirmations' | 'done';
export type ConfirmationTab = 'webTeam' | 'contentTeam' | 'accountProfile' | 'recapEmail';

export interface UseMeetingNoteRecordingOptions {
	/** Pre-selected account (from FindRecordingsModal context) */
	preSelectedAccount?: IAccount | null;
	/** Called when the entire flow finishes */
	onComplete?: () => void;
}

export function useMeetingNoteRecording({ preSelectedAccount, onComplete }: UseMeetingNoteRecordingOptions = {}) {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('MeetingNoteRecording');

	// Phase state machine
	const [phase, setPhase] = React.useState<RecordingPhase>('idle');

	// Selected transcript and meeting data
	const [selectedTranscript, setSelectedTranscript] = React.useState<ITranscriptMatch | null>(null);
	const [meetingWithTranscripts, setMeetingWithTranscripts] = React.useState<ICalendarMeetingWithTranscripts | null>(
		null
	);

	// Account search with debounce
	const [debouncedAccountSearch, setDebouncedAccountSearch] = React.useState('');
	const debouncedAccountSearchCallback = useDebounceCallback((value: string) => {
		setDebouncedAccountSearch(value);
	}, 300);
	const handleAccountSearchValueChange = React.useCallback(
		(value: string) => {
			setAccountSearchValue(value);
			debouncedAccountSearchCallback(value);
		},
		[debouncedAccountSearchCallback]
	);

	// Account filter for search
	const accountFilterRequest = React.useMemo(() => {
		const criteria: AdminApi.IAccountFilterCriteria[] = [];
		if (debouncedAccountSearch && debouncedAccountSearch.length > 0) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Name,
				value: debouncedAccountSearch,
			});
		}
		return {
			criteria,
			op: Api.FilterOperator.And,
		} as AdminApi.IAccountsFilterRequest;
	}, [debouncedAccountSearch]);

	const accountsQuery = useAdminAccountFilterQuery({
		filter: accountFilterRequest,
		autocompleteFragment: debouncedAccountSearch || undefined,
		pageSize: 50,
	});

	const accounts = React.useMemo(() => {
		if (!accountsQuery.data) return [];
		return accountsQuery.data.pages.flatMap(page => page.values || []);
	}, [accountsQuery.data]);

	// Meeting note form state
	const [selectedAccount, setSelectedAccount] = React.useState<IAccount | null>(preSelectedAccount || null);
	const [accountSearchValue, setAccountSearchValue] = React.useState(preSelectedAccount?.companyName || '');
	const [accountIdToFetch, setAccountIdToFetch] = React.useState<string | null>(null);
	const [meetingNoteContentState, setMeetingNoteContentState] = React.useState<IRichContentEditorState | null>(null);
	const [meetingType, setMeetingType] = React.useState<CsmMeetingTypes | null>(null);
	const [meetingStatus, setMeetingStatus] = React.useState('Completed');
	const [accountStage, setAccountStage] = React.useState<string | null>(
		preSelectedAccount?.additionalInfo?.stage || null
	);
	const [duration, setDuration] = React.useState<number>(DEFAULT_MEETING_DURATION_MINUTES);
	const [isOnboarding, setIsOnboarding] = React.useState(false);
	const [nextMeetingDate, setNextMeetingDate] = React.useState<Date | null>(null);
	const [showNextMeetingDatePicker, setShowNextMeetingDatePicker] = React.useState(false);
	const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
	const [isSavingNote, setIsSavingNote] = React.useState(false);
	const [didSkipMeetingNote, setDidSkipMeetingNote] = React.useState(false);

	// Team type selection (persisted in localStorage)
	const [selectedTeamType, setSelectedTeamType] = useLocalStorage<TeamType>({
		key: TEAM_TYPE_STORAGE_KEY,
		initialValue: 'SuccessClientMeeting',
	});

	// Track logged meetings
	const { markMeetingLogged } = useMeetingLogStorage();

	// Load all users (for CC lookup)
	const { users } = useLoadAllUsers({ pageSize: 500 });
	const activeUsers = React.useMemo(() => {
		return users.filter((user: IUser) => user.activationStatus === 'ACTIVE');
	}, [users]);

	// Query to fetch full account data when resolving from attendees
	const fullAccountQuery = useAdminAccountQuery({
		accountId: accountIdToFetch || '',
		enabled: !!accountIdToFetch,
	});

	// Set selected account when full account data is fetched
	React.useEffect(() => {
		if (fullAccountQuery.data) {
			setSelectedAccount(fullAccountQuery.data);
			setAccountSearchValue(fullAccountQuery.data.companyName || '');
			setAccountStage(fullAccountQuery.data.additionalInfo?.stage || null);
			setAccountIdToFetch(null);
		}
	}, [fullAccountQuery.data]);

	// CSM user lookup for Implementation team CC notice
	const csmUserForCcNotice = React.useMemo(() => {
		if (selectedTeamType !== 'ImplementationMeeting' || !selectedAccount?.additionalInfo?.customerSuccessId) {
			return null;
		}
		return activeUsers?.find(u => u.id === selectedAccount.additionalInfo?.customerSuccessId) ?? null;
	}, [selectedTeamType, selectedAccount?.additionalInfo?.customerSuccessId, activeUsers]);

	// Web Team confirmation state
	const [showWebTeamConfirmation, setShowWebTeamConfirmation] = React.useState(false);
	const [webTeamSummary, setWebTeamSummary] = React.useState<string | null>(null);
	const [isGeneratingWebTeamSummary, setIsGeneratingWebTeamSummary] = React.useState(false);
	const [webTeamBusinessType, setWebTeamBusinessType] = React.useState('');
	const [webTeamLocation, setWebTeamLocation] = React.useState('');
	const [webTeamWebsiteUrl, setWebTeamWebsiteUrl] = React.useState('');

	// Content Team confirmation state
	const [showContentTeamConfirmation, setShowContentTeamConfirmation] = React.useState(false);
	const [contentTeamSummary, setContentTeamSummary] = React.useState<string | null>(null);
	const [isGeneratingContentTeamSummary, setIsGeneratingContentTeamSummary] = React.useState(false);
	const [contentTeamWebsiteUrl, setContentTeamWebsiteUrl] = React.useState('');
	const [contentTeamContentState, setContentTeamContentState] = React.useState<IRichContentEditorState | null>(null);
	const [isSubmittingContentTeamNote, setIsSubmittingContentTeamNote] = React.useState(false);

	// Account Profile confirmation state
	const [showAccountProfileConfirmation, setShowAccountProfileConfirmation] = React.useState(false);
	const [accountProfileWebsiteUrl, setAccountProfileWebsiteUrl] = React.useState('');
	const [isGeneratingAccountProfile, setIsGeneratingAccountProfile] = React.useState(false);
	const [generatedBusinessDescription, setGeneratedBusinessDescription] = React.useState('');
	const [generatedKeywords, setGeneratedKeywords] = React.useState('');
	const [generatedContentGoals, setGeneratedContentGoals] = React.useState('');
	const [generatedLocation, setGeneratedLocation] = React.useState('');
	const [generatedAddress, setGeneratedAddress] = React.useState('');
	const [generatedPhoneNumber, setGeneratedPhoneNumber] = React.useState('');
	const [generatedPronouns, setGeneratedPronouns] = React.useState('');
	const [generatedTonePreference, setGeneratedTonePreference] = React.useState('');
	const [isSavingAccountProfile, setIsSavingAccountProfile] = React.useState(false);

	// Tab flow
	const [activeConfirmationTab, setActiveConfirmationTab] = React.useState<ConfirmationTab | null>(null);
	const [completedTabs, setCompletedTabs] = React.useState<Set<ConfirmationTab>>(new Set());

	// Recap Email state
	const [showRecapEmailConfirmation, setShowRecapEmailConfirmation] = React.useState(false);
	const [recapEmailContentState, setRecapEmailContentState] = React.useState<IRichContentEditorState | null>(null);
	const [recapEmailSubject, setRecapEmailSubject] = React.useState('');
	const [recapEmailRecipients, setRecapEmailRecipients] = React.useState('');
	const [isGeneratingRecapEmail, setIsGeneratingRecapEmail] = React.useState(false);
	const [isSendingRecapEmail, setIsSendingRecapEmail] = React.useState(false);

	// Mutations
	const summarizeMutation = useAdminTranscriptSummarizeMutation();
	const saveMeetingNoteMutation = useAdminAccountMeetingNoteMutation();
	const contentNotesGenerateMutation = useAdminContentNotesGenerateMutation();
	const contentNotesSaveMutation = useAdminContentNotesSaveMutation();
	const websiteUrlMutation = useAdminAccountWebsiteUrlMutation();
	const accountProfileGenerateMutation = useAdminAccountProfileGenerateMutation();
	const accountProfileCreateMutation = useAccountProfileCreateMutation();
	const accountProfileUpdateMutation = useAccountProfileUpdateMutation();

	// Account Profile query
	const accountProfileImpersonationContext = React.useMemo(
		() => (selectedAccount?.id ? { account: { id: selectedAccount.id } } : undefined),
		[selectedAccount?.id]
	);
	const accountProfileQuery = useAccountProfileQuery({
		impersonationContext: accountProfileImpersonationContext,
	});

	// Compute website URL from account data
	const computeWebsiteUrl = React.useCallback((account: IAccount | null): string => {
		if (!account) return '';
		const additionalInfoUrl = account.additionalInfo?.websiteUrl;
		const featuresUrl = account.features?.websites?.websiteUrl;
		const emailDomain = account.emailDomain;
		const emailDomainUrl = emailDomain && emailDomain !== '@none.com' ? `https://${emailDomain}` : '';
		return additionalInfoUrl || featuresUrl || emailDomainUrl;
	}, []);

	// Resolve account from meeting attendees
	const resolveAccountFromAttendees = React.useCallback(
		(meetingData: ICalendarMeetingWithTranscripts | null) => {
			if (preSelectedAccount) return; // Skip resolution if account already known
			const attendees = meetingData?.meeting?.attendees || [];
			const attendeeWithAccount = attendees.find(
				a => !a.isOrganizer && a.accountId && a.companyName && !RealMagicAccountIds.has(a.accountId)
			);
			if (attendeeWithAccount && attendeeWithAccount.accountId) {
				setAccountIdToFetch(attendeeWithAccount.accountId);
			}
		},
		[preSelectedAccount]
	);

	// Start recording flow
	const startRecording = React.useCallback(
		async (transcript: ITranscriptMatch, meetingData: ICalendarMeetingWithTranscripts) => {
			if (!transcript.transcriptContent) return;

			setSelectedTranscript(transcript);
			setMeetingWithTranscripts(meetingData);
			setPhase('summarizing');

			try {
				const attendeeNames =
					meetingData.meeting?.attendees?.map(a => a.userName || a.name || a.email || 'Unknown') || [];

				const summaryResult = await summarizeMutation.mutateAsync({
					transcriptContent: transcript.transcriptContent,
					attendeeNames,
					meetingType: selectedTeamType,
					industry: (preSelectedAccount || selectedAccount)?.additionalInfo?.industry,
				});

				const summaryHtml = markdownToEditorHtml(summaryResult?.summary || '');
				setMeetingNoteContentState(summaryHtml ? createContentStateWithHtmlStringValue(summaryHtml) : null);
				setDuration(transcript.durationMinutes || DEFAULT_MEETING_DURATION_MINUTES);
				setMeetingStatus('Completed');
				setValidationErrors([]);

				resolveAccountFromAttendees(meetingData);
				setPhase('form');
			} catch (error) {
				logApiError('RecordMeetingNote-Error', error);
				errorMessages?.pushApiError(error);
				setPhase('idle');
			}
		},
		[
			summarizeMutation,
			selectedTeamType,
			preSelectedAccount,
			selectedAccount,
			resolveAccountFromAttendees,
			logApiError,
			errorMessages,
		]
	);

	// Validate form
	const validateForm = React.useCallback(() => {
		const errors: string[] = [];
		if (!selectedAccount) {
			errors.push('Please select an account.');
		}
		if (!meetingNoteContentState?.hasContent()) {
			errors.push('Please enter meeting notes.');
		}
		if (!meetingType) {
			errors.push('Please select a meeting type.');
		}
		setValidationErrors(errors);
		return errors.length === 0;
	}, [selectedAccount, meetingNoteContentState, meetingType]);

	// Helper to set up confirmation tabs
	const setupConfirmationTabs = React.useCallback(
		(account: IAccount) => {
			const isImplementationMeeting = selectedTeamType === 'ImplementationMeeting';
			const isWebsiteAccount = !isImplementationMeeting && account.features?.websites?.enabled === true;
			const isContentCalendarAccount =
				!isImplementationMeeting && account.features?.customContentCalendar?.enabled === true;
			const needsAccountProfile = !isImplementationMeeting && !hasContentPreferences(accountProfileQuery.data);
			const shouldShowRecapEmail = !isImplementationMeeting;

			const computedWebsiteUrl = computeWebsiteUrl(account);

			if (isWebsiteAccount || isContentCalendarAccount || needsAccountProfile || shouldShowRecapEmail) {
				setCompletedTabs(new Set());

				if (isWebsiteAccount) {
					setActiveConfirmationTab('webTeam');
				} else if (isContentCalendarAccount) {
					setActiveConfirmationTab('contentTeam');
				} else if (needsAccountProfile) {
					setActiveConfirmationTab('accountProfile');
				} else if (shouldShowRecapEmail) {
					setActiveConfirmationTab('recapEmail');
				}

				if (isWebsiteAccount) {
					setShowWebTeamConfirmation(true);
					setWebTeamBusinessType(account.additionalInfo?.industry || '');
					const address = account.planDetails?.address;
					const location = address?.city && address?.stateProvince ? `${address.city}, ${address.stateProvince}` : '';
					setWebTeamLocation(location);
					setWebTeamWebsiteUrl(computedWebsiteUrl);
				}

				if (isContentCalendarAccount) {
					setShowContentTeamConfirmation(true);
					setContentTeamWebsiteUrl(computedWebsiteUrl);
				}

				if (needsAccountProfile) {
					setShowAccountProfileConfirmation(true);
					setAccountProfileWebsiteUrl(computedWebsiteUrl);
				}

				if (shouldShowRecapEmail) {
					setShowRecapEmailConfirmation(true);
				}

				setPhase('confirmations');
				return true;
			}
			return false;
		},
		[selectedTeamType, accountProfileQuery.data, computeWebsiteUrl]
	);

	// Save meeting note
	const handleSaveMeetingNote = React.useCallback(async () => {
		if (!validateForm()) return;

		setIsSavingNote(true);

		try {
			const meetingTags: string[] = [];
			if (isOnboarding) {
				meetingTags.push('Onboarding');
			}

			let ccUser: Api.IUser | undefined;
			if (selectedTeamType === 'ImplementationMeeting' && selectedAccount?.additionalInfo?.customerSuccessId) {
				ccUser = activeUsers?.find(u => u.id === selectedAccount.additionalInfo?.customerSuccessId);
			}

			const rawContent = meetingNoteContentState?.getRawRichTextContent();
			const noteHtml = rawContent ? getContentHtmlStringValueFromRawRichTextContentState(rawContent) : '';

			await saveMeetingNoteMutation.mutateAsync({
				account: selectedAccount!,
				meetingType: meetingType!,
				meetingStatus,
				durationInMinutes: duration,
				meetingTags,
				noteHtml,
				ccUser,
			});

			toast.success('Meeting note saved successfully!');

			// Update next meeting date if set
			if (nextMeetingDate && selectedAccount?.id) {
				try {
					await userSession.webServiceHelper.callAsync<Api.IAccount>(
						`admin/account/${selectedAccount.id}/additionalInfo/nextMeeting?nextMeeting=${encodeURIComponent(moment(nextMeetingDate).format('MM/DD/YYYY'))}`,
						'PUT'
					);
				} catch (error) {
					logApiError('UpdateNextMeetingDate-Error', error);
					toast.error('Meeting note saved, but failed to update next meeting date');
				}
			}

			// Update account stage if set
			if (accountStage && selectedAccount?.id) {
				try {
					await userSession.webServiceHelper.callAsync<Api.IAccount>(
						`admin/account/${selectedAccount.id}/additionalInfo/stage?stage=${encodeURIComponent(accountStage)}`,
						'PUT'
					);
				} catch (error) {
					logApiError('UpdateAccountStage-Error', error);
					toast.error('Meeting note saved, but failed to update account stage');
				}
			}

			// Track logged meeting
			if (meetingWithTranscripts?.meeting?.id) {
				markMeetingLogged(meetingWithTranscripts.meeting.id);
			}

			setDidSkipMeetingNote(false);

			// Set up confirmation tabs or complete
			if (!setupConfirmationTabs(selectedAccount!)) {
				setPhase('done');
				onComplete?.();
			}
		} catch (error) {
			logApiError('SaveMeetingNote-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSavingNote(false);
		}
	}, [
		validateForm,
		selectedAccount,
		meetingType,
		meetingStatus,
		accountStage,
		duration,
		isOnboarding,
		meetingNoteContentState,
		saveMeetingNoteMutation,
		logApiError,
		errorMessages,
		nextMeetingDate,
		userSession,
		activeUsers,
		selectedTeamType,
		markMeetingLogged,
		meetingWithTranscripts,
		setupConfirmationTabs,
		onComplete,
	]);

	// Skip meeting note and go to confirmations (or done if no account)
	const handleSkipToConfirmation = React.useCallback(() => {
		setValidationErrors([]);
		setDidSkipMeetingNote(true);

		if (!selectedAccount || !setupConfirmationTabs(selectedAccount)) {
			setPhase('done');
			onComplete?.();
		}
	}, [selectedAccount, setupConfirmationTabs, onComplete]);

	// Navigate to next tab or close
	const navigateToNextTabOrClose = React.useCallback(
		(justCompletedTab: ConfirmationTab) => {
			const tabOrder: { tab: ConfirmationTab; isVisible: boolean }[] = [
				{ tab: 'webTeam', isVisible: showWebTeamConfirmation },
				{ tab: 'contentTeam', isVisible: showContentTeamConfirmation },
				{ tab: 'accountProfile', isVisible: showAccountProfileConfirmation },
				{ tab: 'recapEmail', isVisible: showRecapEmailConfirmation },
			];

			const updatedCompletedTabs = new Set(completedTabs).add(justCompletedTab);
			const nextTab = tabOrder.find(t => t.isVisible && !updatedCompletedTabs.has(t.tab));

			if (nextTab) {
				setActiveConfirmationTab(nextTab.tab);
			} else {
				setShowWebTeamConfirmation(false);
				setShowContentTeamConfirmation(false);
				setShowAccountProfileConfirmation(false);
				setShowRecapEmailConfirmation(false);
				setActiveConfirmationTab(null);
				setCompletedTabs(new Set());
				setPhase('done');
				onComplete?.();
			}
		},
		[
			showWebTeamConfirmation,
			showContentTeamConfirmation,
			showAccountProfileConfirmation,
			showRecapEmailConfirmation,
			completedTabs,
			onComplete,
		]
	);

	// Web Team handlers
	const handleGenerateWebTeamSummary = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent) return;

		setIsGeneratingWebTeamSummary(true);
		try {
			const attendeeNames =
				meetingWithTranscripts?.meeting?.attendees?.map(a => a.userName || a.name || a.email || 'Unknown') || [];

			const summaryResult = await summarizeMutation.mutateAsync({
				transcriptContent: selectedTranscript.transcriptContent,
				attendeeNames,
				meetingType: 'SuccessClientMeetingForWebTeam',
				industry: selectedAccount?.additionalInfo?.industry,
				deepResearchAccountId: selectedAccount?.id,
				deepResearchBusinessName: selectedAccount?.companyName,
				deepResearchBusinessType: webTeamBusinessType || undefined,
				deepResearchLocation: webTeamLocation || undefined,
				deepResearchWebsiteUrl: webTeamWebsiteUrl || undefined,
			});

			setWebTeamSummary(summaryResult?.summary || '');

			if (!selectedAccount?.additionalInfo?.websiteUrl && webTeamWebsiteUrl && selectedAccount?.id) {
				try {
					await websiteUrlMutation.mutateAsync({
						accountId: selectedAccount.id,
						websiteUrl: webTeamWebsiteUrl,
					});
					toast.success('Website URL saved to account');
				} catch (urlError) {
					logApiError('SaveWebsiteUrl-Error', urlError);
				}
			}
		} catch (error) {
			logApiError('GenerateWebTeamSummary-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingWebTeamSummary(false);
		}
	}, [
		selectedTranscript,
		meetingWithTranscripts,
		selectedAccount,
		summarizeMutation,
		webTeamBusinessType,
		webTeamLocation,
		webTeamWebsiteUrl,
		websiteUrlMutation,
		logApiError,
		errorMessages,
	]);

	const handleSkipWebTeamSummary = React.useCallback(() => {
		setShowWebTeamConfirmation(false);
		setWebTeamSummary(null);
		setWebTeamBusinessType('');
		setWebTeamLocation('');
		setWebTeamWebsiteUrl('');
		setCompletedTabs(prev => new Set(prev).add('webTeam'));
		navigateToNextTabOrClose('webTeam');
	}, [navigateToNextTabOrClose]);

	const handleCopyWebTeamSummary = React.useCallback(() => {
		if (webTeamSummary) {
			navigator.clipboard.writeText(webTeamSummary);
			toast.success('Summary copied to clipboard!');
		}
	}, [webTeamSummary]);

	// Content Team handlers
	const handleGenerateContentTeamSummary = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent || !selectedAccount?.id || !contentTeamWebsiteUrl) return;

		setIsGeneratingContentTeamSummary(true);
		try {
			const generateResult = await contentNotesGenerateMutation.mutateAsync({
				transcriptContent: selectedTranscript.transcriptContent,
				accountId: selectedAccount.id,
				businessName: selectedAccount.companyName || '',
				websiteUrl: contentTeamWebsiteUrl,
			});

			// Response field is named `htmlContent` but is markdown the backend
			// has wrapped in <lev-content>/<p style="margin:0;..."> blocks.
			const contentHtml = markdownToEditorHtml(generateResult?.htmlContent || '');
			setContentTeamContentState(contentHtml ? createContentStateWithHtmlStringValue(contentHtml) : null);
			setContentTeamSummary('generated');

			if (!selectedAccount?.additionalInfo?.websiteUrl && contentTeamWebsiteUrl) {
				try {
					await websiteUrlMutation.mutateAsync({
						accountId: selectedAccount.id,
						websiteUrl: contentTeamWebsiteUrl,
					});
					toast.success('Website URL saved to account');
				} catch (urlError) {
					logApiError('SaveWebsiteUrl-Error', urlError);
				}
			}
		} catch (error) {
			logApiError('GenerateContentTeamSummary-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingContentTeamSummary(false);
		}
	}, [
		selectedTranscript,
		selectedAccount,
		contentNotesGenerateMutation,
		contentTeamWebsiteUrl,
		websiteUrlMutation,
		logApiError,
		errorMessages,
	]);

	const handleSkipContentTeamSummary = React.useCallback(() => {
		setShowContentTeamConfirmation(false);
		setContentTeamSummary(null);
		setContentTeamContentState(null);
		setContentTeamWebsiteUrl('');
		setCompletedTabs(prev => new Set(prev).add('contentTeam'));
		navigateToNextTabOrClose('contentTeam');
	}, [navigateToNextTabOrClose]);

	const handleSubmitContentTeamNote = React.useCallback(async () => {
		if (!contentTeamContentState || !selectedAccount?.id) return;

		setIsSubmittingContentTeamNote(true);
		try {
			const rawContent = contentTeamContentState.getRawRichTextContent();
			const htmlContent = getContentHtmlStringValueFromRawRichTextContentState(rawContent);

			await contentNotesSaveMutation.mutateAsync({
				htmlContent,
				accountId: selectedAccount.id,
				businessName: selectedAccount.companyName || '',
				industry: selectedAccount.additionalInfo?.industry || undefined,
			});

			toast.success('Content notes saved and sent to ClickUp!');
			setShowContentTeamConfirmation(false);
			setContentTeamSummary(null);
			setContentTeamContentState(null);
			setContentTeamWebsiteUrl('');
			navigateToNextTabOrClose('contentTeam');
		} catch (error) {
			logApiError('SubmitContentTeamNote-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSubmittingContentTeamNote(false);
		}
	}, [
		contentTeamContentState,
		selectedAccount,
		contentNotesSaveMutation,
		logApiError,
		errorMessages,
		navigateToNextTabOrClose,
	]);

	// Account Profile handlers
	const handleGenerateAccountProfile = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent || !selectedAccount?.id) return;

		setIsGeneratingAccountProfile(true);
		try {
			const result = await accountProfileGenerateMutation.mutateAsync({
				accountId: selectedAccount.id,
				transcriptContent: selectedTranscript.transcriptContent,
				websiteUrl: accountProfileWebsiteUrl || undefined,
			});

			setGeneratedBusinessDescription(result.businessDescription || '');
			setGeneratedKeywords(result.keywords || '');
			setGeneratedContentGoals(result.contentGoals || '');
			setGeneratedLocation(result.location || '');
			setGeneratedAddress(result.address || '');
			setGeneratedPhoneNumber(result.phoneNumber || '');
			setGeneratedPronouns(result.pronouns || '');
			setGeneratedTonePreference(result.tonePreference || '');

			if (!selectedAccount?.additionalInfo?.websiteUrl && accountProfileWebsiteUrl) {
				try {
					await websiteUrlMutation.mutateAsync({
						accountId: selectedAccount.id,
						websiteUrl: accountProfileWebsiteUrl,
					});
					toast.success('Website URL saved to account');
				} catch (urlError) {
					logApiError('SaveWebsiteUrl-Error', urlError);
				}
			}
		} catch (error) {
			logApiError('GenerateAccountProfile-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingAccountProfile(false);
		}
	}, [
		selectedTranscript,
		selectedAccount,
		accountProfileGenerateMutation,
		accountProfileWebsiteUrl,
		websiteUrlMutation,
		logApiError,
		errorMessages,
	]);

	const handleSaveAccountProfile = React.useCallback(async () => {
		if (!selectedAccount?.id) return;

		setIsSavingAccountProfile(true);
		try {
			const profileData: Partial<Api.AccountProfile> = {
				businessDescription: generatedBusinessDescription,
				location: generatedLocation,
				address: generatedAddress,
				phoneNumber: generatedPhoneNumber,
				pronouns: generatedPronouns,
				tonePreference: generatedTonePreference,
				contentGoals: generatedContentGoals,
			};
			if (generatedKeywords) {
				profileData.messagingPreferences = { keywords: generatedKeywords };
			}

			if (accountProfileQuery.data?.id) {
				await accountProfileUpdateMutation.mutateAsync({
					accountProfile: {
						...accountProfileQuery.data,
						...profileData,
					},
					impersonationContext: accountProfileImpersonationContext,
				});
			} else {
				await accountProfileCreateMutation.mutateAsync({
					accountProfile: { id: null, ...profileData } as Api.AccountProfile,
					impersonationContext: accountProfileImpersonationContext,
				});
			}

			invalidateAccountProfileQueries({ impersonationContext: accountProfileImpersonationContext });
			toast.success('AI Custom Content preferences saved!');

			setShowAccountProfileConfirmation(false);
			setGeneratedBusinessDescription('');
			setGeneratedKeywords('');
			setGeneratedContentGoals('');
			setGeneratedLocation('');
			setGeneratedAddress('');
			setGeneratedPhoneNumber('');
			setGeneratedPronouns('');
			setGeneratedTonePreference('');
			setAccountProfileWebsiteUrl('');
			navigateToNextTabOrClose('accountProfile');
		} catch (error) {
			logApiError('SaveAccountProfile-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSavingAccountProfile(false);
		}
	}, [
		selectedAccount,
		generatedBusinessDescription,
		generatedKeywords,
		generatedContentGoals,
		generatedLocation,
		generatedAddress,
		generatedPhoneNumber,
		generatedPronouns,
		generatedTonePreference,
		accountProfileQuery.data,
		accountProfileCreateMutation,
		accountProfileUpdateMutation,
		accountProfileImpersonationContext,
		logApiError,
		errorMessages,
		navigateToNextTabOrClose,
	]);

	const handleSkipAccountProfile = React.useCallback(() => {
		setShowAccountProfileConfirmation(false);
		setGeneratedBusinessDescription('');
		setGeneratedKeywords('');
		setGeneratedContentGoals('');
		setGeneratedLocation('');
		setGeneratedAddress('');
		setGeneratedPhoneNumber('');
		setGeneratedPronouns('');
		setGeneratedTonePreference('');
		setAccountProfileWebsiteUrl('');
		setCompletedTabs(prev => new Set(prev).add('accountProfile'));
		navigateToNextTabOrClose('accountProfile');
	}, [navigateToNextTabOrClose]);

	// Recap Email handlers
	const handleGenerateRecapEmail = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent) return;

		setIsGeneratingRecapEmail(true);
		try {
			const attendeeNames =
				meetingWithTranscripts?.meeting?.attendees?.map(a => a.userName || a.name || a.email || 'Unknown') || [];

			const result = await summarizeMutation.mutateAsync({
				transcriptContent: selectedTranscript.transcriptContent,
				attendeeNames,
				meetingType: 'SuccessClientMeetingRecapEmail',
				industry: selectedAccount?.additionalInfo?.industry,
			});

			const summaryHtml = result?.summary ? result.summary.replace(/\n/g, '<br>') : null;
			setRecapEmailContentState(summaryHtml ? createContentStateWithHtmlStringValue(summaryHtml) : null);
			setRecapEmailSubject(`Follow-up: ${meetingWithTranscripts?.meeting?.summary || 'Our Recent Meeting'}`);

			const attendeeEmails = (meetingWithTranscripts?.meeting?.attendees || [])
				.filter(a => !a.isOrganizer && a.email)
				.map(a => a.email)
				.join(', ');
			setRecapEmailRecipients(attendeeEmails);
		} catch (error) {
			logApiError('GenerateRecapEmail-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingRecapEmail(false);
		}
	}, [selectedTranscript, meetingWithTranscripts, selectedAccount, summarizeMutation, logApiError, errorMessages]);

	const handleSendRecapEmail = React.useCallback(async () => {
		if (!recapEmailContentState?.hasContent()) return;

		if (!recapEmailRecipients.trim()) {
			toast.error('Please enter at least one recipient email address');
			return;
		}

		setIsSendingRecapEmail(true);
		try {
			const rawContent = recapEmailContentState.getRawRichTextContent();
			const emailBodyHtml = rawContent ? getContentHtmlStringValueFromRawRichTextContentState(rawContent) : '';

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const recipientsArray = recapEmailRecipients
				.split(/[,;]/)
				.map(email => email.trim())
				.filter(email => email.length > 0);

			const invalidEmails = recipientsArray.filter(email => !emailRegex.test(email));
			if (invalidEmails.length > 0) {
				toast.error(`Invalid email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.join(', ')}`);
				setIsSendingRecapEmail(false);
				return;
			}

			const recipientCount = await userSession.webServiceHelper.callAsync<number>(
				'admin/userOperations/sendRecapEmail',
				'POST',
				{
					recipients: recipientsArray,
					subject: recapEmailSubject,
					emailBody: emailBodyHtml,
				}
			);
			if (recipientCount === 0) {
				toast.error('No emails were sent. Please check the recipient addresses.');
				return;
			}
			toast.success(`Recap email sent to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}!`);
			setRecapEmailContentState(null);
			setRecapEmailSubject('');
			setRecapEmailRecipients('');
			setCompletedTabs(prev => new Set(prev).add('recapEmail'));
			navigateToNextTabOrClose('recapEmail');
		} catch (error) {
			logApiError('SendRecapEmail-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSendingRecapEmail(false);
		}
	}, [
		recapEmailContentState,
		recapEmailSubject,
		recapEmailRecipients,
		userSession,
		logApiError,
		errorMessages,
		navigateToNextTabOrClose,
	]);

	const handleSkipRecapEmail = React.useCallback(() => {
		setRecapEmailContentState(null);
		setRecapEmailSubject('');
		setRecapEmailRecipients('');
		setCompletedTabs(prev => new Set(prev).add('recapEmail'));
		navigateToNextTabOrClose('recapEmail');
	}, [navigateToNextTabOrClose]);

	// Reset all state
	const resetAll = React.useCallback(() => {
		setPhase('idle');
		setSelectedTranscript(null);
		setMeetingWithTranscripts(null);
		setSelectedAccount(preSelectedAccount || null);
		setAccountSearchValue(preSelectedAccount?.companyName || '');
		setAccountIdToFetch(null);
		setMeetingNoteContentState(null);
		setMeetingType(null);
		setMeetingStatus('Completed');
		setAccountStage(preSelectedAccount?.additionalInfo?.stage || null);
		setDuration(DEFAULT_MEETING_DURATION_MINUTES);
		setIsOnboarding(false);
		setNextMeetingDate(null);
		setShowNextMeetingDatePicker(false);
		setValidationErrors([]);
		setDidSkipMeetingNote(false);
		setShowWebTeamConfirmation(false);
		setWebTeamSummary(null);
		setWebTeamBusinessType('');
		setWebTeamLocation('');
		setWebTeamWebsiteUrl('');
		setShowContentTeamConfirmation(false);
		setContentTeamSummary(null);
		setContentTeamContentState(null);
		setContentTeamWebsiteUrl('');
		setShowAccountProfileConfirmation(false);
		setAccountProfileWebsiteUrl('');
		setGeneratedBusinessDescription('');
		setGeneratedKeywords('');
		setGeneratedContentGoals('');
		setGeneratedLocation('');
		setGeneratedAddress('');
		setGeneratedPhoneNumber('');
		setGeneratedPronouns('');
		setGeneratedTonePreference('');
		setShowRecapEmailConfirmation(false);
		setRecapEmailContentState(null);
		setRecapEmailSubject('');
		setRecapEmailRecipients('');
		setActiveConfirmationTab(null);
		setCompletedTabs(new Set());
	}, [preSelectedAccount]);

	return {
		// Phase
		phase,

		// Form state
		selectedAccount,
		setSelectedAccount,
		accountSearchValue,
		handleAccountSearchValueChange,
		accounts,
		accountsQuery,
		meetingNoteContentState,
		setMeetingNoteContentState,
		meetingType,
		setMeetingType,
		meetingStatus,
		setMeetingStatus,
		accountStage,
		setAccountStage,
		duration,
		setDuration,
		isOnboarding,
		setIsOnboarding,
		nextMeetingDate,
		setNextMeetingDate,
		showNextMeetingDatePicker,
		setShowNextMeetingDatePicker,
		validationErrors,
		isSavingNote,
		selectedTeamType,
		setSelectedTeamType,
		csmUserForCcNotice,
		didSkipMeetingNote,

		// Transcript data
		selectedTranscript,
		meetingWithTranscripts,

		// Web Team
		showWebTeamConfirmation,
		webTeamSummary,
		isGeneratingWebTeamSummary,
		webTeamBusinessType,
		setWebTeamBusinessType,
		webTeamLocation,
		setWebTeamLocation,
		webTeamWebsiteUrl,
		setWebTeamWebsiteUrl,
		handleGenerateWebTeamSummary,
		handleSkipWebTeamSummary,
		handleCopyWebTeamSummary,

		// Content Team
		showContentTeamConfirmation,
		contentTeamSummary,
		isGeneratingContentTeamSummary,
		contentTeamWebsiteUrl,
		setContentTeamWebsiteUrl,
		contentTeamContentState,
		setContentTeamContentState,
		isSubmittingContentTeamNote,
		handleGenerateContentTeamSummary,
		handleSkipContentTeamSummary,
		handleSubmitContentTeamNote,

		// Account Profile
		showAccountProfileConfirmation,
		accountProfileWebsiteUrl,
		setAccountProfileWebsiteUrl,
		isGeneratingAccountProfile,
		generatedBusinessDescription,
		setGeneratedBusinessDescription,
		generatedKeywords,
		setGeneratedKeywords,
		generatedContentGoals,
		setGeneratedContentGoals,
		generatedLocation,
		setGeneratedLocation,
		generatedAddress,
		setGeneratedAddress,
		generatedPhoneNumber,
		setGeneratedPhoneNumber,
		generatedPronouns,
		setGeneratedPronouns,
		generatedTonePreference,
		setGeneratedTonePreference,
		isSavingAccountProfile,
		handleGenerateAccountProfile,
		handleSaveAccountProfile,
		handleSkipAccountProfile,

		// Recap Email
		showRecapEmailConfirmation,
		recapEmailContentState,
		setRecapEmailContentState,
		recapEmailSubject,
		setRecapEmailSubject,
		recapEmailRecipients,
		setRecapEmailRecipients,
		isGeneratingRecapEmail,
		isSendingRecapEmail,
		handleGenerateRecapEmail,
		handleSendRecapEmail,
		handleSkipRecapEmail,

		// Tab flow
		activeConfirmationTab,
		setActiveConfirmationTab,
		completedTabs,

		// Actions
		startRecording,
		handleSaveMeetingNote,
		handleSkipToConfirmation,
		resetAll,

		// Re-export constants for consumers
		summarizeMutation,
	};
}
